import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import fetchAccountLogo from '@Application/Branding/AccountLogo/fetchAccountLogo';
import FetchAccountLogo from '@Application/Branding/AccountLogo/Queries/FetchAccountLogo';
import ErrorState from '@Guidelines/Component/ErrorState';
import { Skeleton } from '@verclam/ui-kit';
import LogoPreview from '@UI/Pages/Administration/Organizations/Shared/LogoPreview';
import AccountLogoCardActions from '@UI/Pages/Administration/Organizations/AccountLogoCard/AccountLogoCardActions';
import { css } from '@emotion/react';
import { theme } from '@Guidelines/Theme/theme';

const cardStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1.5rem;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.border};
	height: 100%;
`;

const titleStyle = css`
	${theme.typography.metric}
	margin: 0;
	color: ${theme.colors.ink};
`;

const subtitleStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkMuted};
`;

const DEFAULT_LOAD_ERROR_MESSAGE = "Le logo du compte n'a pas pu être chargé.";

/**
 * The account-level logo block, gated on `ACCOUNT_LOGO_WRITE`. The page mounts it as an
 * unconditional sibling of the organizations list, so the list's own loading/error/empty branches
 * never hide it. The fetch lives in the gated child: holders of other permissions never fire it.
 */
const AccountLogoCard: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;

	const {
		data: accountLogo,
		error,
		refetch,
	} = useStoreQuery({
		selector: (state) => state.brandingStore.accountLogo,
		stopOn: (value) => value !== null && value.ownerId === accountId,
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchAccountLogo(new FetchAccountLogo(accountId)));
		},
		deps: [accountId],
	});

	const isCurrent = accountLogo !== null && accountLogo.ownerId === accountId;

	const renderBody = (): React.ReactNode => {
		if (error !== null) {
			return <ErrorState title={DEFAULT_LOAD_ERROR_MESSAGE} onRetry={refetch} />;
		}

		if (!isCurrent) {
			return <Skeleton width="15rem" height="4rem" />;
		}

		return (
			<React.Fragment>
				{accountLogo.dataUrl !== null ? (
					<LogoPreview dataUrl={accountLogo.dataUrl} alt="Logo du compte" />
				) : (
					<p css={subtitleStyle}>Aucun logo : le logo de l'application est affiché.</p>
				)}
				<AccountLogoCardActions hasLogo={accountLogo.hasImage} />
			</React.Fragment>
		);
	};

	return (
		<section css={cardStyle} aria-label="Logo du compte">
			<div>
				<h2 css={titleStyle}>Logo du compte</h2>
				<p css={subtitleStyle}>Affiché par toutes les structures qui n'ont pas leur propre logo.</p>
			</div>
			{renderBody()}
		</section>
	);
};

export default AccountLogoCard;
